import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Container,
  Grid,
  Tabs,
  Tab,
  Chip,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { CustomContainedButton } from "./OutlinedButton";
import { getPilgrimagesForSlider } from "../services/sanityApi";

export const PilgrimageCard = ({ article }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/articole/${article.slug}`);
  };

  const truncatedExcerpt =
    article.excerpt && article.excerpt.length > 120
      ? article.excerpt.substring(0, 120) + "..."
      : article.excerpt || "";

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxShadow: "none",
        border: "1px solid lightgrey",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {article.featuredImage ? (
        <CardMedia
          component="img"
          image={article.featuredImage}
          alt={article.title}
          sx={{ height: 220, objectFit: "cover" }}
        />
      ) : (
        <Box
          sx={{
            height: 220,
            background: "linear-gradient(135deg, #800020 0%, #BE9257 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Libre Baskerville",
              fontSize: "2rem",
              color: "rgba(255,255,255,0.6)",
            }}
          >
            ✝
          </Typography>
        </Box>
      )}

      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          p: 3,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          {article.date && (
            <Chip
              label={article.date}
              size="small"
              sx={{
                backgroundColor: "#FEF8E8",
                color: "#800020",
                fontFamily: "Inter",
                fontSize: "0.75rem",
                border: "1px solid #BE9257",
              }}
            />
          )}
          {article.cost && (
            <Chip
              label={article.cost}
              size="small"
              sx={{
                backgroundColor: "#FEF8E8",
                color: "#BE9257",
                fontFamily: "Inter",
                fontSize: "0.75rem",
                border: "1px solid #BE9257",
                fontWeight: 400,
              }}
            />
          )}
        </Box>

        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontFamily: "Libre Baskerville",
            fontWeight: 700,
            fontSize: { xs: "0.95rem", sm: "1rem" },
            mb: 1.5,
            lineHeight: 1.4,
            color: "#1a1a1a",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {article.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontFamily: "Inter",
            color: "#555",
            lineHeight: 1.6,
            fontSize: "0.875rem",
            flexGrow: 1,
            mb: 2.5,
          }}
        >
          {truncatedExcerpt}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CustomContainedButton
            onClick={handleClick}
            text="Citește mai mult"
            sx={{ px: 2.5 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

const CardSkeleton = () => (
  <Card
    sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      borderRadius: "8px",
      border: "1px solid #f0ece4",
      overflow: "hidden",
    }}
  >
    <Skeleton variant="rectangular" height={220} />
    <CardContent sx={{ p: 3 }}>
      <Skeleton height={24} width="60%" sx={{ mb: 1.5 }} />
      <Skeleton height={20} />
      <Skeleton height={20} />
      <Skeleton height={20} width="80%" />
    </CardContent>
  </Card>
);

const PilgrimageSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("pelerinaje-externe");
  const [pilgrimagesExterne, setPilgrimagesExterne] = useState([]);
  const [pilgrimagesInterne, setPilgrimagesInterne] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getPilgrimagesForSlider("pelerinaje-externe", 9),
      getPilgrimagesForSlider("pelerinaje-interne", 9),
    ])
      .then(([externe, interne]) => {
        setPilgrimagesExterne(externe);
        setPilgrimagesInterne(interne);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setCurrentIndex(0);
  };

  const pageSize = isMobile ? 1 : 3;

  const filteredPilgrimages =
    selectedCategory === "pelerinaje-externe"
      ? pilgrimagesExterne
      : pilgrimagesInterne;

  const totalPages = Math.ceil(filteredPilgrimages.length / pageSize) || 1;

  return (
    <Container maxWidth="lg" sx={{ py: 4, width: "100%" }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{
          pt: { xs: 3, md: 7 },
          fontFamily: "Libre Baskerville",
          fontWeight: 700,
          width: { xs: "100%", md: "600px" },
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        Deschide porțile către o călătorie a sufletului
      </Typography>
      <Typography
        variant="body2"
        component="p"
        gutterBottom
        sx={{
          pb: { xs: 3, md: 5 },
          pt: 2,
          fontFamily: "Inter",
          width: { xs: "100%", md: "600px" },
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        Pelerinajul religios este o căutare în lumea aceasta a ceea ce nu este
        din lumea aceasta „Împărăția lui Dumnezeu"
      </Typography>

      <Box sx={{ borderBottom: 0, borderColor: "divider", mb: 4 }}>
        <Tabs
          value={selectedCategory}
          onChange={handleTabChange}
          centered
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: "Inter",
              fontSize: { xs: "16px", md: "16px" },
              fontWeight: 500,
              color: "text.primary",
              "&.Mui-selected": {
                color: "black",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "black",
            },
          }}
        >
          <Tab
            label="Pelerinaje externe"
            value="pelerinaje-externe"
            sx={{ px: { xs: 2, md: 4 } }}
          />
          <Tab
            label="Pelerinaje interne"
            value="pelerinaje-interne"
            sx={{ px: { xs: 2, md: 4 } }}
          />
        </Tabs>
      </Box>

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: pageSize }).map((_, i) => (
            <Grid item xs={12} sm={pageSize === 1 ? 12 : 6} md={4} key={i}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : filteredPilgrimages.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ fontFamily: "Inter", color: "#888" }}>
            Nu există pelerinaje publicate momentan.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() =>
              setCurrentIndex((prev) =>
                prev === 0 ? totalPages - 1 : prev - 1
              )
            }
            sx={{
              position: "absolute",
              left: { xs: -8, sm: -20 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "black",
              boxShadow: 0,
              "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.85355 3.14645C7.04882 3.34171 7.04882 3.65829 6.85355 3.85355L3.70711 7H12.5C12.7761 7 13 7.22386 13 7.5C13 7.77614 12.7761 8 12.5 8H3.70711L6.85355 11.1464C7.04882 11.3417 7.04882 11.6583 6.85355 11.8536C6.65829 12.0488 6.34171 12.0488 6.14645 11.8536L2.14645 7.85355C1.95118 7.65829 1.95118 7.34171 2.14645 7.14645L6.14645 3.14645C6.34171 2.95118 6.65829 2.95118 6.85355 3.14645Z"
                fill="white"
                fillRule="evenodd"
                clipRule="evenodd"
              ></path>
            </svg>
          </IconButton>

          <Box sx={{ overflow: "hidden", mx: { xs: 2, sm: 4 } }}>
            <Box
              sx={{
                display: "flex",
                transition: "transform 0.3s ease-in-out",
                transform: `translateX(-${currentIndex * 100}%)`,
                mb: 2,
              }}
            >
              {Array.from({ length: totalPages }).map((_, pageIndex) => (
                <Grid
                  container
                  spacing={2}
                  key={pageIndex}
                  sx={{ flexShrink: 0, width: "100%" }}
                >
                  {filteredPilgrimages
                    .slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)
                    .map((pilgrimage) => (
                      <Grid
                        item
                        xs={12}
                        sm={pageSize === 1 ? 12 : 6}
                        md={pageSize === 3 ? 4 : 6}
                        key={pilgrimage.id}
                      >
                        <PilgrimageCard article={pilgrimage} />
                      </Grid>
                    ))}
                </Grid>
              ))}
            </Box>
          </Box>

          <IconButton
            onClick={() =>
              setCurrentIndex((prev) =>
                prev === totalPages - 1 ? 0 : prev + 1
              )
            }
            sx={{
              position: "absolute",
              right: { xs: -8, sm: -20 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "black",
              boxShadow: 0,
              "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.14645 3.14645C8.34171 2.95118 8.65829 2.95118 8.85355 3.14645L12.8536 7.14645C13.0488 7.34171 13.0488 7.65829 12.8536 7.85355L8.85355 11.8536C8.65829 12.0488 8.34171 12.0488 8.14645 11.8536C7.95118 11.6583 7.95118 11.3417 8.14645 11.1464L11.2929 8H2.5C2.22386 8 2 7.77614 2 7.5C2 7.22386 2.22386 7 2.5 7H11.2929L8.14645 3.85355C7.95118 3.65829 7.95118 3.34171 8.14645 3.14645Z"
                fill="white"
                fillRule="evenodd"
                clipRule="evenodd"
              ></path>
            </svg>
          </IconButton>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              mt: 2,
            }}
          >
            {Array.from({ length: totalPages }).map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentIndex(index)}
                sx={{
                  width: currentIndex === index ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: currentIndex === index ? "black" : "grey.300",
                  transition: "all 0.3s ease-in-out",
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default PilgrimageSlider;
